import { useState, useEffect, useRef, useCallback } from 'react';

interface VideoItem {
  id: string;
  url: string;
  cover: string;
  title: string;
  like: number;
  comment: number;
  share: number;
  author: string;
  avatar: string;
}

export default function DouyinPremiumFeed() {
  const [videoList, setVideoList] = useState<VideoItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [showComment, setShowComment] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<VideoItem | null>(null);
  const [retryMap, setRetryMap] = useState<Record<string, number>>({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [isPortraitLocked, setIsPortraitLocked] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const playingRef = useRef<HTMLVideoElement | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const fullScreenRef = useRef<HTMLDivElement>(null);
  
  // 滚轮事件节流时间戳
  const lastWheelTime = useRef<number>(0);
  
  // 观看行为追踪
  const watchStartTime = useRef<number | null>(null);
  const hasSentAnalytics = useRef<boolean>(false);
  const currentVideoId = useRef<string | null>(null);
  
  // 滑动控制 - 使用 touch 事件手动控制，完全避免浏览器惯性滚动
  const touchStartY = useRef<number>(0);
  const touchStartTime = useRef<number>(0);
  const isTransitioning = useRef<boolean>(false); // 切换动画锁，必须严格防止连续切换
  const TRANSITION_LOCK_DURATION = 600; // 切换后锁定输入 600ms
  const feedRef = useRef<HTMLDivElement>(null);
  
  // 使用 ref 存储 activeIndex，避免事件处理函数闭包过时
  const activeIndexRef = useRef<number>(0);
  const accumulatedWheelDelta = useRef<number>(0);
  const lastWheelDirection = useRef<number>(0);
  const wheelResetTimer = useRef<any>(null);
  
  // 同步 activeIndex 到 ref
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);
  
  // 播放设置
  // V6.0: 全局播放设置（从服务器加载）
  const [playbackSettings, setPlaybackSettings] = useState<{ loopPlayback: boolean; swipeSensitivity: number }>({ loopPlayback: false, swipeSensitivity: 100 });
  
  useEffect(() => {
    const fetchPlaybackSettings = async () => {
      try {
        const response = await fetch('/api/settings/playback');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            setPlaybackSettings({
              loopPlayback: data.settings.loopPlayback || false,
              swipeSensitivity: data.settings.swipeSensitivity || 100
            });
          }
        }
      } catch (e) {
        console.error('获取全局播放设置失败:', e);
      }
    };
    fetchPlaybackSettings();
  }, []);
  
  // 根据灵敏度计算滑动阈值：1-100 映射到 50%-5% 的屏幕高度比例
  const getSwipeThreshold = useCallback(() => {
    const ratio = 0.5 - (playbackSettings.swipeSensitivity / 100) * 0.45;
    return window.innerHeight * ratio;
  }, [playbackSettings.swipeSensitivity]);

  // 发送观看统计到后端
  const sendWatchAnalytics = useCallback(async (videoId: string, watchDuration: number, totalDuration: number, isCompleted: boolean) => {
    try {
      await fetch(`/api/videos/${videoId}/analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          watchDuration,
          totalDuration,
          isCompleted
        })
      });
      console.log(`[Analytics] Video ${videoId}: watched ${Math.round(watchDuration/1000)}s / ${Math.round(totalDuration/1000)}s, completed: ${isCompleted}`);
    } catch (err) {
      console.error('[Analytics] Failed to send:', err);
    }
  }, []);

  // V6.0: 从服务器加载视频（使用全局播放设置）
  const fetchVideos = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // V6.0: 从 API 获取全局播放设置
      let settingsResponse;
      try {
        settingsResponse = await fetch('/api/settings/playback');
      } catch (e) {
        console.error('获取全局播放设置失败，使用默认值', e);
      }
      
      let playbackSettings = { shuffle: true, group: 'all', recommend: false, coldVideoInsert: false, coldVideoInterval: 5, maxColdVideos: 3 };
      if (settingsResponse && settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings) {
          playbackSettings = settingsData.settings;
        }
      }
      
      const params = new URLSearchParams();
        
        // 如果开启了智能推荐，调用推荐 API
        if (playbackSettings.recommend) {
          const response = await fetch('/api/recommendations?limit=20');
          if (!response.ok) {
            throw new Error('Failed to fetch recommendations');
          }
          const data = await response.json();
          
          let formattedVideos: VideoItem[] = data.videos?.map((v: any) => ({
            id: v.id,
            url: v.url,
            cover: v.thumbnail || `https://picsum.photos/1080/1920?random=${v.id}`,
            title: v.title || v.filename || '未命名视频',
            like: v.likeCount || Math.floor(Math.random() * 5000),
            comment: v.commentCount || Math.floor(Math.random() * 300),
            share: v.shareCount || Math.floor(Math.random() * 100),
            author: v.author || '本地用户',
            avatar: `https://picsum.photos/100/100?random=${v.id}`
          })) || [];
          
          // 如果开启了冷门视频插播，获取并插入冷门视频
          if (playbackSettings.coldVideoInsert) {
            try {
              const coldResponse = await fetch('/api/recommendations/cold?limit=5&maxViews=10&completionRate=0.3');
              if (coldResponse.ok) {
                const coldData = await coldResponse.json();
                const coldVideos: VideoItem[] = coldData.videos?.map((v: any) => ({
                  id: v.id,
                  url: v.url,
                  cover: v.thumbnail || `https://picsum.photos/1080/1920?random=${v.id}`,
                  title: `[探索] ${v.title || v.filename || '未命名视频'}`,
                  like: v.likeCount || Math.floor(Math.random() * 5000),
                  comment: v.commentCount || Math.floor(Math.random() * 300),
                  share: v.shareCount || Math.floor(Math.random() * 100),
                  author: v.author || '本地用户',
                  avatar: `https://picsum.photos/100/100?random=${v.id}`
                })) || [];
                
                // 每5个视频插入1个冷门视频，最多插入3个
                const insertInterval = playbackSettings.coldVideoInterval || 5;
                const maxColdVideos = playbackSettings.maxColdVideos || 3;
                
                let coldIndex = 0;
                let insertedCount = 0;
                for (let i = insertInterval; i < formattedVideos.length && coldIndex < coldVideos.length && insertedCount < maxColdVideos; i += insertInterval + 1) {
                  formattedVideos.splice(i, 0, coldVideos[coldIndex]);
                  coldIndex++;
                  insertedCount++;
                }
                
                console.log(`[Cold Videos] Inserted ${insertedCount} cold videos into feed`);
              }
            } catch (err) {
              console.error('[Cold Videos] Failed to fetch:', err);
            }
          }
          
          setVideoList(formattedVideos);
          setError(null);
        } else {
          // 否则使用普通视频列表 API
          // 默认开启随机播放，除非用户明确关闭
          if (playbackSettings.shuffle === false) {
            // 不添加 shuffle 参数
          } else {
            params.append('shuffle', 'true');
          }
          if (playbackSettings.group && playbackSettings.group !== 'all') {
            params.append('group', playbackSettings.group);
          }
          
          const url = params.toString() ? `/api/videos?${params.toString()}` : '/api/videos';
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error('Failed to fetch videos');
          }
          const data = await response.json();
          
          const formattedVideos: VideoItem[] = data.map((v: any) => ({
            id: v.id,
            url: v.url,
            cover: v.thumbnail || `https://picsum.photos/1080/1920?random=${v.id}`,
            title: v.title || v.filename || '未命名视频',
            like: v.likeCount || Math.floor(Math.random() * 5000),
            comment: v.commentCount || Math.floor(Math.random() * 300),
            share: v.shareCount || Math.floor(Math.random() * 100),
            author: v.author || '本地用户',
            avatar: `https://picsum.photos/100/100?random=${v.id}`
          }));
          
          setVideoList(formattedVideos);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('加载视频失败，请检查服务器');
      } finally {
        setIsLoading(false);
      }
  }, []);

  // V6.0: 初始化时加载视频
  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // 监听当前活跃视频的播放事件
  useEffect(() => {
    const currentVideoId_str = videoList[activeIndex]?.id;
    const videoElement = currentVideoId_str ? videoRefs.current[currentVideoId_str] : null;
    if (!videoElement) {
      console.warn('[Video Events] No video element found for index', activeIndex, 'id', currentVideoId_str);
      return;
    }

    const handlePlay = () => {
      watchStartTime.current = Date.now();
      hasSentAnalytics.current = false;
      currentVideoId.current = currentVideoId_str || null;
    };

    const handlePause = () => {
      if (watchStartTime.current && !hasSentAnalytics.current && currentVideoId.current) {
        const watchDuration = Date.now() - watchStartTime.current;
        const totalDuration = videoElement.duration || 0;
        sendWatchAnalytics(currentVideoId.current, watchDuration, totalDuration, false);
        hasSentAnalytics.current = true;
      }
    };

    const handleEnded = () => {
      if (watchStartTime.current && !hasSentAnalytics.current && currentVideoId.current) {
        const watchDuration = Date.now() - watchStartTime.current;
        const totalDuration = videoElement.duration || 0;
        sendWatchAnalytics(currentVideoId.current, watchDuration, totalDuration, true);
        hasSentAnalytics.current = true;
      }
      
      // 如果开启了循环播放，则重新播放当前视频
      if (playbackSettings.loopPlayback) {
        console.log('[Loop Playback] Enabled, restarting video...');
        videoElement.currentTime = 0;
        // 尝试播放，如果被浏览器阻止则静默处理
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.warn('[Loop Playback] Auto-play blocked by browser policy, will retry muted:', err.message);
            // 如果被阻止，尝试静音后重新播放
            const wasMuted = videoElement.muted;
            videoElement.muted = true;
            videoElement.play().then(() => {
              console.log('[Loop Playback] Played successfully in muted mode');
            }).catch((err2) => {
              console.error('[Loop Playback] Play failed even muted:', err2.message);
            }).finally(() => {
              // 恢复原来的静音状态（延迟一点避免闪烁）
              setTimeout(() => { videoElement.muted = wasMuted; }, 100);
            });
          });
        }
      } else {
        console.log('[Loop Playback] Disabled, will auto-scroll to next');
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && watchStartTime.current && !hasSentAnalytics.current && currentVideoId.current) {
        const watchDuration = Date.now() - watchStartTime.current;
        const totalDuration = videoElement.duration || 0;
        sendWatchAnalytics(currentVideoId.current, watchDuration, totalDuration, false);
        hasSentAnalytics.current = true;
      }
    };

    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // 切换视频时发送统计
      if (watchStartTime.current && !hasSentAnalytics.current && currentVideoId.current) {
        const watchDuration = Date.now() - watchStartTime.current;
        const totalDuration = videoElement.duration || 0;
        sendWatchAnalytics(currentVideoId.current, watchDuration, totalDuration, false);
      }
    };
  }, [activeIndex, videoList, sendWatchAnalytics, playbackSettings.loopPlayback]);

  // 横竖屏适配 - 竖屏视频用 contain 保持比例，横屏视频用 cover 填满
  const getVideoFit = (): 'contain' | 'cover' => {
    // V6.1: 全屏时始终使用 contain，保持视频原始比例，不强制横屏
    if (isFullScreen) {
      return 'contain';
    }
    // 非全屏模式：手机竖屏用 cover，平板/电脑横屏用 contain
    return window.innerWidth > window.innerHeight ? 'contain' : 'cover';
  };
  const [objectFit, setObjectFit] = useState<'contain' | 'cover'>(getVideoFit());

  // 屏幕旋转监听
  useEffect(() => {
    const updateOrientation = () => {
      setObjectFit(getVideoFit());
    };

    window.addEventListener('resize', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);
    updateOrientation();

    return () => {
      window.removeEventListener('resize', updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []);

  // 全屏控制 - V6.1: 浏览器全屏 + 方向锁定
  const enterFullScreen = useCallback(() => {
    // 请求浏览器全屏（隐藏地址栏），不改变视频和UI
    const docEl = document.documentElement;
    if (docEl.requestFullscreen) {
      docEl.requestFullscreen().catch(() => {});
    } else if ((docEl as any).webkitRequestFullscreen) {
      (docEl as any).webkitRequestFullscreen();
    } else if ((docEl as any).msRequestFullscreen) {
      (docEl as any).msRequestFullscreen();
    }
    
    // V6.1: 根据用户选择锁定屏幕方向
    const orientation = (screen as any).orientation;
    if (orientation && orientation.lock) {
      if (isPortraitLocked) {
        // 锁定竖屏
        orientation.lock('portrait').catch(() => {});
      } else {
        // 自动旋转模式：根据当前视频方向动态锁定
        const currentVideoId = videoList[activeIndex]?.id;
        const video = currentVideoId ? videoRefs.current[currentVideoId] : null;
        let isLandscape = false;
        
        if (video && video.videoWidth > 0 && video.videoHeight > 0) {
          isLandscape = video.videoWidth > video.videoHeight;
        }
        
        // 根据视频方向锁定：横屏视频锁定landscape，竖屏视频锁定portrait
        orientation.lock(isLandscape ? 'landscape' : 'portrait').catch(() => {});
      }
    }
    
    setIsFullScreen(true);
    setIsLocked(true);
  }, [videoList, activeIndex, isPortraitLocked]);

  const exitFullScreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    
    // V6.1: 解锁方向
    try {
      const orientation = (screen as any).orientation;
      if (orientation && orientation.unlock) {
        orientation.unlock();
      }
    } catch (e) {
      // 忽略不支持的情况
    }
    
    setIsFullScreen(false);
    setIsLocked(false);
  }, []);

  // V6.1: 切换方向锁定状态
  const togglePortraitLock = useCallback(() => {
    setIsPortraitLocked(prev => {
      const newValue = !prev;
      // 如果当前正在全屏且锁定状态改变，实时应用
      if (isFullScreen) {
        const orientation = (screen as any).orientation;
        if (newValue) {
          // 锁定竖屏
          if (orientation && orientation.lock) {
            orientation.lock('portrait').catch(() => {});
          }
        } else {
          // 解除锁定
          if (orientation && orientation.unlock) {
            orientation.unlock();
          }
        }
      }
      return newValue;
    });
  }, [isFullScreen]);
  useEffect(() => {
    const handleFullscreenChange = () => {
      const nowFullScreen = !!document.fullscreenElement;
      setIsFullScreen(nowFullScreen);
      if (nowFullScreen) setIsLocked(true);
      setObjectFit(getVideoFit());
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // V6.1: 当 isFullScreen 变化时，更新 objectFit
  useEffect(() => {
    setObjectFit(getVideoFit());
  }, [isFullScreen]);

  // 拦截 ESC 键（全屏锁定时阻止退出）
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullScreen && isLocked) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen, isLocked]);

  // 手动控制滚动切换 - 完全替代 CSS scroll-snap，避免浏览器惯性滚动
  useEffect(() => {
    const feed = feedRef.current;
    if (!feed || videoList.length === 0) return;

    const handleTouchStart = (e: TouchEvent) => {
      // 如果正在切换动画中，阻止所有新的触摸输入
      if (isTransitioning.current) {
        e.preventDefault();
        return;
      }
      touchStartY.current = e.touches[0].clientY;
      touchStartTime.current = Date.now();
    };

    const handleTouchMove = (e: TouchEvent) => {
      // 切换动画期间阻止滑动
      if (isTransitioning.current) {
        e.preventDefault();
        return;
      }
      
      const currentY = e.touches[0].clientY;
      const deltaY = touchStartY.current - currentY;
      
      // 当垂直滑动距离超过 5px 时，阻止浏览器原生滚动，避免与 scrollIntoView 冲突
      if (Math.abs(deltaY) > 5) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // 切换动画期间忽略所有输入
      if (isTransitioning.current) {
        e.preventDefault();
        return;
      }

      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY.current - touchEndY; // 正值表示向上滑（下一个视频）
      const deltaTime = Date.now() - touchStartTime.current;

      // 滑动距离和速度判断
      const threshold = getSwipeThreshold();

      // 当滑动距离超过阈值时切换（不再限制速度，快速滑动也应响应）
      if (Math.abs(deltaY) > threshold) {
        const currentIdx = activeIndexRef.current;
        let newIndex = currentIdx;

        if (deltaY > 0 && currentIdx < videoList.length - 1) {
          // 向上滑 -> 下一个视频
          newIndex = currentIdx + 1;
        } else if (deltaY < 0 && currentIdx > 0) {
          // 向下滑 -> 上一个视频
          newIndex = currentIdx - 1;
        }

        if (newIndex !== currentIdx) {
          // 锁定输入，开始切换
          isTransitioning.current = true;

          // 执行滚动切换
          const targetVideo = feed.children[newIndex] as HTMLElement;
          if (targetVideo) {
            targetVideo.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }

          // 更新活跃索引
          setActiveIndex(newIndex);

          // 延迟释放锁，确保动画完成且惯性滚动结束
          setTimeout(() => {
            isTransitioning.current = false;
          }, TRANSITION_LOCK_DURATION);
        }
      }
    };

    // 同时处理鼠标滚轮事件
    const handleWheel = (e: WheelEvent) => {
      if (isTransitioning.current) {
        e.preventDefault();
        return;
      }

      const now = Date.now();
      if (now - lastWheelTime.current < TRANSITION_LOCK_DURATION) {
        e.preventDefault();
        return;
      }

      // 方向改变时重置累积值
      if ((lastWheelDirection.current > 0 && e.deltaY < 0) || 
          (lastWheelDirection.current < 0 && e.deltaY > 0)) {
        accumulatedWheelDelta.current = 0;
      }
      lastWheelDirection.current = e.deltaY;
      accumulatedWheelDelta.current += e.deltaY;

      // 200ms 无新滚轮事件则重置累积值
      if (wheelResetTimer.current) {
        clearTimeout(wheelResetTimer.current);
      }
      wheelResetTimer.current = setTimeout(() => {
        accumulatedWheelDelta.current = 0;
        lastWheelDirection.current = 0;
      }, 200);

      const currentIdx = activeIndexRef.current;
      let newIndex = currentIdx;
      const wheelThreshold = 80;

      if (accumulatedWheelDelta.current > wheelThreshold && currentIdx < videoList.length - 1) {
        newIndex = currentIdx + 1;
        accumulatedWheelDelta.current = 0;
      } else if (accumulatedWheelDelta.current < -wheelThreshold && currentIdx > 0) {
        newIndex = currentIdx - 1;
        accumulatedWheelDelta.current = 0;
      }

      if (newIndex !== currentIdx) {
        e.preventDefault();
        isTransitioning.current = true;
        lastWheelTime.current = now;

        const targetVideo = feed.children[newIndex] as HTMLElement;
        if (targetVideo) {
          targetVideo.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        setActiveIndex(newIndex);

        setTimeout(() => {
          isTransitioning.current = false;
        }, TRANSITION_LOCK_DURATION);
      }
    };

    // 必须 passive: false 才能 preventDefault
    feed.addEventListener('touchstart', handleTouchStart, { passive: false });
    feed.addEventListener('touchmove', handleTouchMove, { passive: false });
    feed.addEventListener('touchend', handleTouchEnd, { passive: false });
    feed.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      feed.removeEventListener('touchstart', handleTouchStart);
      feed.removeEventListener('touchmove', handleTouchMove);
      feed.removeEventListener('touchend', handleTouchEnd);
      feed.removeEventListener('wheel', handleWheel);
      if (wheelResetTimer.current) {
        clearTimeout(wheelResetTimer.current);
      }
    };
  }, [videoList.length, getSwipeThreshold]);

  // 视频播放控制（只观察可见范围内的视频）
  useEffect(() => {
    const videos = Object.values(videoRefs.current).filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            video.play().catch(() => {});
            playingRef.current = video;
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      },
      { threshold: [0.6] }
    );

    videos.forEach((video) => video && observer.observe(video));
    return () => observer.disconnect();
  }, [videoList, activeIndex]);

  // 加载更多
  const loadMore = useCallback(() => {
    console.log('Load more videos...');
  }, []);

  // 视频错误处理
  const handleVideoError = (video: HTMLVideoElement, id: string) => {
    setRetryMap((prev) => {
      const count = (prev[id] || 0) + 1;
      if (count < 3) {
        setTimeout(() => {
          video.load();
          video.play().catch(() => {});
        }, 1000 * count);
      }
      return { ...prev, [id]: count };
    });
  };

  // 视频播放结束 - 自动播放下一个或循环播放
  const handleVideoEnded = (id: string) => {
    // 如果开启了循环播放，不执行任何操作（由 handleEnded 处理）
    if (playbackSettings.loopPlayback) {
      return;
    }
    
    const currentEl = feedRef.current?.querySelector(`[data-id="${id}"]`);
    if (!currentEl) return;
    
    const nextEl = currentEl.nextElementSibling as HTMLElement | null;
    if (nextEl && nextEl.classList.contains('video-item')) {
      nextEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // 同步更新 activeIndex，确保视频渲染和播放状态正确
      const nextIndex = Array.from(feedRef.current?.children || []).indexOf(nextEl);
      if (nextIndex !== -1) {
        setActiveIndex(nextIndex);
      }
    }
  };

  // 播放/暂停切换
  const togglePlay = (video: HTMLVideoElement | null) => {
    if (!video) return;
    video.paused ? video.play().catch(() => {}) : video.pause();
  };

  // 双击处理 - 进入全屏 + 爱心动画（全屏后需解锁才能退出）
  const handleDoubleClick = (e: React.MouseEvent, item: VideoItem) => {
    if (!isFullScreen) {
      enterFullScreen();
      setIsLocked(true);
    }

    // 爱心动画
    const heart = (e.target as HTMLElement).closest('.video-item')?.querySelector('.heart');
    if (heart) {
      heart.classList.remove('active');
      void (heart as HTMLElement).offsetWidth;
      heart.classList.add('active');
    }
  };

  // 解锁并退出全屏
  const handleUnlock = () => {
    setIsLocked(false);
    exitFullScreen();
  };

  // 格式化数字
  const formatNumber = (num: number): string => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + 'w';
    }
    return num.toString();
  };

  if (isLoading) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #fff4', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s infinite linear', margin: '0 auto 16px' }} />
          加载中...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (videoList.length === 0) {
    return (
      <div style={{ width: '100vw', height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎬</div>
          <div>暂无视频</div>
          <div style={{ fontSize: 14, color: '#999', marginTop: 8 }}>请先上传一些视频</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={fullScreenRef}
      style={{ width: '100vw', height: '100vh', background: '#000', overflow: 'hidden' }}
    >
      <div
        ref={feedRef}
        onScroll={(e) => {
          const t = e.currentTarget as HTMLDivElement;
          if (t.scrollHeight - t.scrollTop - t.clientHeight < 200) loadMore();
        }}
        style={{ height: '100%', overflowY: 'auto', touchAction: 'pan-y' }}
      >
        {videoList.map((item, index) => {
          const isActive = index >= activeIndex - 3 && index <= activeIndex + 3;
          return (
            <div
              key={item.id}
              id={`video-container-${item.id}`}
              data-id={item.id}
              className="video-item"
              style={{
                height: '100vh',
                position: 'relative',
                width: '100%',
                background: '#000',
                flexShrink: 0
              }}
              onClick={(e: React.MouseEvent) => {
                if (isActive && !(e.target as HTMLElement).closest('.btn-panel')) {
                  togglePlay(playingRef.current);
                }
              }}
              onDoubleClick={(e: React.MouseEvent) => isActive && handleDoubleClick(e, item)}
            >
              {isActive ? (
                <>
                  <img
                    src={item.cover}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 }}
                    alt="cover"
                  />

                  <div
                    className="loading"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      zIndex: 2,
                      width: 40,
                      height: 40,
                      border: '3px solid #fff4',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 1s infinite linear',
                      transform: 'translate(-50%, -50%)'
                    }}
                  />

                  {(retryMap[item.id] || 0) >= 2 && (
                    <div style={{ position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 99, color: '#fff', background: '#000a', padding: '8px 16px', borderRadius: 8 }}>
                      网络较差，点击重试
                    </div>
                  )}

                  <video
                    ref={(el) => { videoRefs.current[item.id] = el as HTMLVideoElement; }}
                    muted={muted}
                    playsInline
                    controls={false}
                    disablePictureInPicture
                    preload="metadata"
                    src={item.url}
                    // V6.1: 鸿蒙浏览器特殊属性
                    webkit-playsinline="true"
                    x5-playsinline="true"
                    x5-video-player-type="h5"
                    x5-video-player-fullscreen="false"
                    x5-video-orientation="portrait"
                    onLoadedData={(e: React.SyntheticEvent<HTMLVideoElement>) => {
                      const video = e.currentTarget;
                      const loading = (video.previousElementSibling as HTMLElement) || null;
                      const cover = (loading?.previousElementSibling as HTMLElement) || null;
                      if (loading) loading.style.display = 'none';
                      if (cover) cover.style.opacity = '0';
                    }}
                    onEnded={() => handleVideoEnded(item.id)}
                    onError={(e) => handleVideoError(e.currentTarget, item.id)}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: objectFit, 
                      position: 'relative', 
                      zIndex: 3,
                      // V6.1: 阻止移动端浏览器自动全屏和旋转，但允许点击事件
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                  />

                  <div
                    className="heart"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      zIndex: 4,
                      width: 80,
                      height: 80,
                      background: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'red\'><path d=\'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z\'/></svg>") no-repeat center',
                      backgroundSize: 'contain',
                      opacity: 0,
                      transform: 'translate(-50%,-50%) scale(0)',
                      pointerEvents: 'none'
                    }}
                  />

                  {/* 方向锁定按钮（左上角，全屏时显示） */}
                  {isFullScreen && (
                    <div
                      onClick={togglePortraitLock}
                      style={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 10,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#fff'
                      }}
                      className="icon-btn"
                      title={isPortraitLocked ? '锁定竖屏（点击解锁）' : '自动旋转（点击锁定竖屏）'}
                    >
                      {isPortraitLocked ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                          <path d="M12 18h.01"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                          <path d="M21 3v5h-5"/>
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                          <path d="M8 21H3v-5"/>
                        </svg>
                      )}
                    </div>
                  )}

                  {/* 左侧标题信息（浏览器全屏时也显示） */}
                    <div style={{ position: 'absolute', left: 15, bottom: 25, zIndex: 10, color: '#fff', textShadow: '0 1px 4px #000', transition: 'opacity 0.3s ease' }}>
                      <div style={{ fontWeight: 'bold', fontSize: 16 }}>{item.title}</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>@{item.author}</div>
                    </div>
                  
                    {/* 右侧按钮面板（浏览器全屏时位置不变） */}
                    <div className="btn-panel" style={{ position: 'absolute', right: 12, bottom: 100, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
                        {/* 头像 */}
                        <div style={{ position: 'relative', marginBottom: 8 }}>
                          <div style={{ width: 50, height: 50, borderRadius: '50%', border: '2px solid #fff', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                            <img src={item.avatar} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="avatar" />
                          </div>
                          <div style={{ position: 'absolute', bottom: -10, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #ff3366, #ff6b6b)', color: '#fff', fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>+</div>
                        </div>

                        {/* 点赞按钮 */}
                        <div style={{ color: '#fff', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} className="icon-btn">
                          <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 4,
                            backdropFilter: 'blur(4px)'
                          }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#ff3366" stroke="#ff3366" strokeWidth="1.5"/>
                            </svg>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{formatNumber(item.like)}</div>
                        </div>

                        {/* 分享按钮 */}
                        <div style={{ color: '#fff', textAlign: 'center', cursor: 'pointer' }} className="icon-btn">
                          <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 4,
                            backdropFilter: 'blur(4px)'
                          }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 500, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{formatNumber(item.share)}</div>
                        </div>

                        {/* 静音按钮 */}
                        <div
                          onClick={() => setMuted(!muted)}
                          style={{
                            color: '#fff',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            backdropFilter: 'blur(4px)'
                          }}
                          className="icon-btn"
                        >
                          {muted ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>

                        {/* V6.0: 不喜欢按钮 */}
                        <div
                          onClick={async () => {
                            try {
                              const response = await fetch('/api/dislikes', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ videoId: item.id })
                              });
                              if (response.ok) {
                                console.log(`已将视频 ${item.id} 标记为不喜欢`);
                                // 立即跳转到下一个视频并刷新列表（排除不喜欢的视频）
                                setActiveIndex(prev => prev + 1);
                                // 延迟一下再刷新列表，确保索引已经更新
                                setTimeout(() => {
                                  fetchVideos();
                                }, 100);
                              }
                            } catch (e) {
                              console.error('标记不喜欢失败:', e);
                              alert('操作失败，请重试');
                            }
                          }}
                          style={{
                            color: '#fff',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            backdropFilter: 'blur(4px)'
                          }}
                          className="icon-btn"
                          title="不喜欢（以后不再播放）"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 14H4a1 1 0 01-1-1v-4a1 1 0 011-1h6l4.707-4.707C15.337 3.663 16 4.109 16 5v14c0 .891-.663 1.337-1.293.707L10 14z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 10l2 2m0 0l-2 2m2-2h-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>

                      </div>
                </>
              ) : (
                <img
                  src={item.cover}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  alt="cover"
                  loading="lazy"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* 全屏解锁退出按钮 */}
      {isFullScreen && isLocked && (
        <button
          onClick={handleUnlock}
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          className="unlock-btn"
          title="点击解锁退出全屏"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </button>
      )}

      {showComment && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 999, background: 'rgba(0,0,0,.7)', display: 'flex', flexDirection: 'column-reverse' }}>
          <div style={{ background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 'bold', marginBottom: 15 }}>评论 {currentVideo?.comment || 0}</div>
            {Array(10).fill(0).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
                <img src={`https://picsum.photos/100/100?random=${i}`} style={{ width: 36, height: 36, borderRadius: '50%' }} alt="avatar" />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 'bold' }}>用户{i}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>评论内容</div>
                </div>
              </div>
            ))}
            <div style={{ height: 40, background: '#f5f5f5', borderRadius: 20, padding: '0 15px', display: 'flex', alignItems: 'center', color: '#999' }}>说点什么...</div>
          </div>
          <div onClick={() => setShowComment(false)} style={{ flex: 1 }} />
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: translate(-50%,-50%) rotate(360deg); } }
        .heart.active { animation: heart .7s forwards; }
        @keyframes heart { 0%{opacity:0;scale:0;}40%{opacity:1;scale:1.2;}100%{opacity:0;scale:.9;} }
        .icon-btn { transition: all 0.2s ease; }
        .icon-btn:active { transform: scale(0.9); }
        .icon-btn:hover { transform: scale(1.05); }
      `}</style>
    </div>
  );
}
